<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 class="text-h5">
        Bulk Reconciliation
      </h2>
      <v-btn
        color="primary"
        depressed
        @click="showNewBatchDialog = true"
      >
        <v-icon left>
          mdi-plus
        </v-icon>
        Open new batch
      </v-btn>
    </div>

    <v-card outlined>
      <v-card-text class="pb-0">
        <v-row dense>
          <v-col
            cols="12"
            sm="4"
          >
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Status"
              multiple
              chips
              clearable
              dense
              outlined
              hide-details
              @change="onFilterChange"
            />
          </v-col>
          <v-col
            cols="12"
            sm="4"
          >
            <v-text-field
              v-model="filters.q"
              label="Search uuid or created-by"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
              @input="onFilterChangeDebounced"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="batches"
        :loading="loading"
        :server-items-length="total"
        :options.sync="tableOptions"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        item-key="uuid"
        @click:row="goToDetail"
      >
        <template #item.status="{ item }">
          <v-chip
            small
            :color="statusColor(item.status)"
            text-color="white"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #item.date_range="{ item }">
          {{ item.start_date }} → {{ item.end_date }}
        </template>
        <template #item.totals="{ item }">
          <span>{{ formatCount(item.total_rows) }}</span>
          <span class="success--text">/ {{ formatCount(item.matched_rows) }}</span>
          <span class="amber--text text--darken-3">/ {{ formatCount(item.unmatched_rows) }}</span>
        </template>
        <template #item.created_at="{ item }">
          {{ formatDateTime(item.created_at) }}
        </template>
        <template #item.closed_at="{ item }">
          {{ item.closed_at ? formatDateTime(item.closed_at) : '—' }}
        </template>
        <template #item.action="{ item }">
          <v-btn
            text
            small
            color="primary"
            :to="`/eod/bulk-reconciliation/${item.uuid}`"
          >
            Open
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <bulk-new-batch-dialog
      v-model="showNewBatchDialog"
      :submitting="creating"
      @submit="onCreateBatch"
    />
  </v-container>
</template>

<script>
import moment from 'moment';
import BulkNewBatchDialog from '@/views/eod/components/bulk/BulkNewBatchDialog.vue';
import { listBatches, createBatch } from '@/store/apis/bulkReconciliation';

const STATUS_OPTIONS = [
  { value: 'draft', text: 'Draft' },
  { value: 'uploading', text: 'Uploading' },
  { value: 'ready_to_match', text: 'Ready to match' },
  { value: 'matching', text: 'Matching' },
  { value: 'matched', text: 'Matched' },
  { value: 'closed', text: 'Closed' },
];

const STATUS_COLORS = {
  draft: 'grey',
  uploading: 'amber darken-1',
  ready_to_match: 'amber darken-2',
  matching: 'blue',
  matched: 'success',
  closed: 'green darken-2',
};

export default {
  name: 'BulkReconciliation',
  components: { BulkNewBatchDialog },
  data() {
    return {
      batches: [],
      total: 0,
      loading: false,
      filters: {
        status: [],
        q: '',
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      showNewBatchDialog: false,
      creating: false,
      filterDebounce: null,
      statusOptions: STATUS_OPTIONS,
      headers: [
        { text: 'Date range', value: 'date_range', sortable: false },
        { text: 'Status', value: 'status' },
        { text: 'Total / Matched / Unmatched', value: 'totals', sortable: false },
        { text: 'Created by', value: 'created_by_name', sortable: false },
        { text: 'Created at', value: 'created_at' },
        { text: 'Closed at', value: 'closed_at', sortable: false },
        {
          text: '', value: 'action', sortable: false, width: '80px',
        },
      ],
    };
  },
  watch: {
    tableOptions: {
      deep: true,
      handler() { this.fetch(); },
    },
  },
  created() {
    this.fetch();
  },
  beforeDestroy() {
    if (this.filterDebounce) clearTimeout(this.filterDebounce);
  },
  methods: {
    formatCount(n) {
      if (n === null || n === undefined) return '—';
      return Number(n).toLocaleString();
    },
    formatDateTime(iso) {
      return iso ? moment(iso).format('YYYY-MM-DD HH:mm') : '—';
    },
    statusLabel(s) {
      const found = STATUS_OPTIONS.find((o) => o.value === s);
      return (found?.text || s || '').toUpperCase();
    },
    statusColor(s) {
      return STATUS_COLORS[s] || 'grey';
    },
    onFilterChange() {
      this.tableOptions.page = 1;
      this.fetch();
    },
    onFilterChangeDebounced() {
      if (this.filterDebounce) clearTimeout(this.filterDebounce);
      this.filterDebounce = setTimeout(() => {
        this.tableOptions.page = 1;
        this.fetch();
      }, 300);
    },
    async fetch() {
      this.loading = true;
      try {
        const params = {
          page: this.tableOptions.page,
          per_page: this.tableOptions.itemsPerPage,
        };
        if (this.filters.status.length) params['status[]'] = this.filters.status;
        if (this.filters.q) params.q = this.filters.q;
        const sortBy = this.tableOptions.sortBy?.[0];
        const sortDesc = this.tableOptions.sortDesc?.[0];
        if (sortBy) params.sort = `${sortDesc ? '-' : ''}${sortBy}`;

        const res = await listBatches(params);
        this.batches = res.data || res.items || [];
        this.total = res.total || res.meta?.total || this.batches.length;
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to load batches.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.loading = false;
      }
    },
    goToDetail(item) {
      this.$router.push(`/eod/bulk-reconciliation/${item.uuid}`);
    },
    async onCreateBatch({ start_date, end_date }) {
      if (this.creating) return;
      this.creating = true;
      try {
        const batch = await createBatch({ start_date, end_date });
        this.showNewBatchDialog = false;
        this.$router.push(`/eod/bulk-reconciliation/${batch.uuid}`);
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to open new batch.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>
